import { useState } from 'react';
import { X, Coins, History, Loader2, ArrowUpCircle, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCreditBalance } from '../hooks/useCreditBalance';
import { useDailyCredits } from '../hooks/useDailyCredits';
import { useTopUpHistory } from '../hooks/useTopUpHistory';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';
import { platform } from '../lib/native';
import { findCreditPackage, purchasePackage, restorePurchases, getNativePrices } from '../lib/revenuecat';
import Modal from './Modal';
import TopUpHistoryItem from './TopUpHistoryItem';
import './UsageModal.css';

const REEDLE_SERVER_URL = import.meta.env.VITE_REEDLE_SERVER_URL || 'https://reedle-server-js.onrender.com';

// Currency configuration
const CURRENCY_CONFIG = {
  usd: { symbol: '$', label: 'USD', prices: { 1000: '1.00', 5000: '5.00' }, paymentMethods: 'Card' },
  gbp: { symbol: '£', label: 'GBP', prices: { 1000: '0.80', 5000: '4.00' }, paymentMethods: 'Card Alipay' },
  cny: { symbol: '¥', label: 'CNY', prices: { 1000: '7.00', 5000: '35.00' }, paymentMethods: 'Card Alipay' },
};

// Format date for display
const formatDate = (dateStr, t) => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const dateOnly = date.toISOString().split('T')[0];
  const todayOnly = today.toISOString().split('T')[0];
  const yesterdayOnly = yesterday.toISOString().split('T')[0];

  if (dateOnly === todayOnly) return t('chat:credits.today');
  if (dateOnly === yesterdayOnly) return t('chat:credits.yesterday');

  // Format as "Dec 24" or "Jan 1"
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Sparkline component - mini chart for last 7 days
const Sparkline = ({ data }) => {
  if (!data || data.length === 0) return null;

  // Take last 7 days
  const last7Days = data.slice(0, 7).reverse();
  const maxValue = Math.max(...last7Days.map(d => d.credits), 1);

  return (
    <div className="sparkline">
      {last7Days.map((day, i) => {
        const height = (day.credits / maxValue) * 100;
        return (
          <div
            key={i}
            className="sparkline-bar"
            style={{ height: `${height}%` }}
          />
        );
      })}
    </div>
  );
};

const UsageModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation(['chat', 'common']);
  const [activeTab, setActiveTab] = useState('balance');
  const [selectedCurrency, setSelectedCurrency] = useState('gbp');
  const { balance, loading: balanceLoading, refresh } = useCreditBalance();
  const { dailyUsage, weekTotal, loading: historyLoading } = useDailyCredits();
  const { history: topUpHistory, loading: topUpLoading } = useTopUpHistory();
  const [purchaseLoading, setPurchaseLoading] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);

  const currencyInfo = CURRENCY_CONFIG[selectedCurrency];
  const nativePrices = platform.isNative ? getNativePrices() : {};

  const handleTopUp = async (credits) => {
    if (platform.isNative) {
      // Native: use RevenueCat / StoreKit
      setPurchaseLoading(credits);
      try {
        const pkg = await findCreditPackage(credits);
        if (!pkg) {
          alert('Product not available. Please try again later.');
          return;
        }
        const result = await purchasePackage(pkg);
        if (result.cancelled) return; // user dismissed payment sheet — no feedback needed
        if (!result.success) {
          alert(result.error || 'Purchase failed. Please try again.');
          return;
        }
        // Poll balance — webhook adds credits asynchronously
        setTimeout(() => refresh(), 3000);
        setTimeout(() => refresh(), 7000);
        setTimeout(() => refresh(), 12000);
        trackEvent('iap_purchase_completed', { credit_amount: credits, platform: platform.name });
      } catch (error) {
        console.error('Native top-up error:', error);
        alert(error.message || 'Purchase failed. Please try again.');
      } finally {
        setPurchaseLoading(null);
      }
      return;
    }

    // Web: use Stripe checkout
    setPurchaseLoading(credits);
    const priceUsd = currencyInfo.prices[credits];
    trackEvent('checkout_initiated', {
      credit_amount: credits,
      price_usd: priceUsd,
      currency: selectedCurrency,
    });

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('Please login to purchase credits.');
        return;
      }

      const response = await fetch(`${REEDLE_SERVER_URL}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ credits, currency: selectedCurrency, app: 'mellow' }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      window.location.href = data.url;
    } catch (error) {
      console.error('Top-up error:', error);
      alert(error.message || 'Failed to start checkout. Please try again.');
    } finally {
      setPurchaseLoading(null);
    }
  };

  const handleRestorePurchases = async () => {
    setRestoreLoading(true);
    try {
      const result = await restorePurchases();
      // Consumable credit packs don't create entitlements, so hasActivePurchases
      // will always be false. We still show feedback as required by §3.1.1.
      alert(result?.hasActivePurchases
        ? 'Purchases restored successfully.'
        : 'No restorable purchases found.');
      // Refresh balance regardless — in case any credits were re-granted server-side
      setTimeout(() => refresh(), 2000);
    } catch (error) {
      console.error('Restore error:', error);
      alert('Restore failed. Please try again.');
    } finally {
      setRestoreLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('chat:credits.modalTitle')}>
      <div className="usage-modal-content">
        {/* Tabs */}
        <div className="usage-tabs">
          <button
            className={`usage-tab ${activeTab === 'balance' ? 'active' : ''}`}
            onClick={() => setActiveTab('balance')}
          >
            <Coins size={16} />
            {t('chat:credits.balance')}
          </button>
          <button
            className={`usage-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <History size={16} />
            {t('chat:credits.history')}
          </button>
          <button
            className={`usage-tab ${activeTab === 'topup' ? 'active' : ''}`}
            onClick={() => setActiveTab('topup')}
          >
            <ArrowUpCircle size={16} />
            {t('chat:credits.topUp')}
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'balance' ? (
          <div className="usage-balance-content">
            {balanceLoading ? (
              <div className="usage-loading"><Loader2 size={24} className="spin" /></div>
            ) : (
              <>
                {/* Negative Balance Warning */}
                {balance < 0 && (
                  <div className="negative-balance-alert">
                    <span className="alert-icon">⚠️</span>
                    <div className="alert-text">
                      <span className="alert-title">{t('chat:credits.negativeBalance')}</span>
                      <span className="alert-subtitle">{t('chat:credits.topUpToContinue')}</span>
                    </div>
                  </div>
                )}

                <div className="usage-balance-display">
                  <span className="balance-value">{balance?.toLocaleString() || '1,000'}</span>
                  <span className="balance-label">{t('chat:credits.creditsLabel')}</span>
                </div>

                <p className="usage-hint">
                  {t('chat:credits.usageHint')}
                </p>

                {/* Currency Selector — web only, native uses store-local pricing */}
                {!platform.isNative && (
                  <div className="currency-selector">
                    {Object.entries(CURRENCY_CONFIG).map(([code, config]) => (
                      <button
                        key={code}
                        className={`currency-option ${selectedCurrency === code ? 'active' : ''}`}
                        onClick={() => setSelectedCurrency(code)}
                      >
                        <span className="currency-symbol">{config.symbol} {config.label}</span>
                        <span className="currency-methods">{config.paymentMethods}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Top-up Options */}
                <div className="topup-options">
                  <button
                    className="topup-btn"
                    onClick={() => handleTopUp(1000)}
                    disabled={purchaseLoading !== null || restoreLoading}
                  >
                    {purchaseLoading === 1000 ? (
                      <Loader2 size={16} className="spin" />
                    ) : platform.isNative ? (
                      <>1,000 Credits{nativePrices['mellow_credits_1000'] ? ` — ${nativePrices['mellow_credits_1000']}` : ''}</>
                    ) : (
                      <>1,000 — {currencyInfo.symbol}{currencyInfo.prices[1000]}</>
                    )}
                  </button>
                  <button
                    className="topup-btn"
                    onClick={() => handleTopUp(5000)}
                    disabled={purchaseLoading !== null || restoreLoading}
                  >
                    {purchaseLoading === 5000 ? (
                      <Loader2 size={16} className="spin" />
                    ) : platform.isNative ? (
                      <>5,000 Credits{nativePrices['mellow_credits_5000'] ? ` — ${nativePrices['mellow_credits_5000']}` : ''}</>
                    ) : (
                      <>5,000 — {currencyInfo.symbol}{currencyInfo.prices[5000]}</>
                    )}
                  </button>
                </div>

                {/* Restore Purchases — required by App Store guidelines §3.1.1, native only */}
                {platform.isNative && (
                  <button
                    className="restore-btn"
                    onClick={handleRestorePurchases}
                    disabled={restoreLoading || purchaseLoading !== null}
                  >
                    {restoreLoading ? (
                      <><Loader2 size={14} className="spin" /> Restoring...</>
                    ) : (
                      <><RotateCcw size={14} /> Restore Purchases</>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        ) : activeTab === 'history' ? (
          <div className="usage-history-content">
            {historyLoading ? (
              <div className="usage-loading"><Loader2 size={24} className="spin" /></div>
            ) : dailyUsage.length === 0 ? (
              <div className="usage-empty">
                <p>{t('chat:credits.noHistory')}</p>
                <p className="usage-empty-hint">{t('chat:credits.noHistoryHint')}</p>
              </div>
            ) : (
              <>
                {/* Week Summary with Sparkline */}
                <div className="week-summary">
                  <div className="week-summary-left">
                    <span className="week-label">{t('chat:credits.last7Days')}</span>
                    <span className="week-total">{t('chat:credits.creditsUsed', { count: weekTotal.toFixed(1) })}</span>
                  </div>
                  <Sparkline data={dailyUsage} />
                </div>

                {/* Daily List */}
                <div className="daily-list">
                  {dailyUsage.map((day) => (
                    <div key={day.date} className="daily-item">
                      <span className="daily-date">{formatDate(day.date, t)}</span>
                      <span className="daily-credits">-{day.credits.toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="usage-topup-content">
            {topUpLoading ? (
              <div className="usage-loading"><Loader2 size={24} className="spin" /></div>
            ) : topUpHistory.length === 0 ? (
              <div className="usage-empty">
                <p>{t('chat:credits.noTopUpHistory')}</p>
                <p className="usage-empty-hint">{t('chat:credits.noTopUpHistoryHint')}</p>
              </div>
            ) : (
              <div className="topup-list">
                {topUpHistory.map((item) => (
                  <TopUpHistoryItem key={item.id} item={item} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default UsageModal;

